import {useEffect, useState} from "react";

export type Listener<T> = (args: T) => void;

export class EventSource<T> {
    private readonly listeners = new Set<Listener<T>>();

    private currentValue?: T;

    get value() {
        return this.currentValue;
    }

    add(listener: Listener<T>) {
        this.listeners.add(listener);
    }

    remove(listener: Listener<T>) {
        this.listeners.delete(listener);
    }

    invokeListeners(args: T) {
        this.currentValue = args;
        for (const listener of this.listeners) {
            listener(args);
        }
    }

    safeInvokeListeners(args: T) {
        this.currentValue = args;
        for (const listener of this.listeners) {
            try {
                listener(args);
            }
            catch (error) {
                console.error("Error invoking listener", error);
            }
        }
    }
}

export function useCurrentEventValue<T>(event: EventSource<T>) {
    const [value, setValue] = useState<T | undefined>(event.value);
    useEffect(() => {
        event.add(setValue);
        return () => event.remove(setValue);
    }, [event]);
    return value;
}